import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import TrackingMap from "@/components/TrackingMap";
import { PackageCheck, Star, KeyRound } from "lucide-react";
import Logo from "@/components/Logo";

const STATUS_STYLE = {
  requested: "bg-slate-100 text-slate-700",
  accepted: "bg-blue-100 text-blue-700",
  picked_up: "bg-indigo-100 text-indigo-700",
  in_transit: "bg-amber-100 text-amber-800",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

const PublicTrack = () => {
  const { token } = useParams();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/track/${token}`);
      if (!r.ok) throw new Error("Not found");
      setT(await r.json());
    } catch { setErr("Tracking link is invalid or expired."); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [token]);

  if (err) return <div className="min-h-screen grid place-items-center text-slate-500">{err}</div>;
  if (!t) return (
    <div className="min-h-screen grid place-items-center text-slate-400">
      <div className="flex flex-col items-center gap-3">
        <Logo size={48} opacity={0.35}/>
        <div className="text-sm">Loading…</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 grid-bg">
      <header className="max-w-2xl mx-auto px-4 py-5 flex items-center gap-2">
        <Logo size={34} variant="dark"/>
        <div className="font-display font-bold">CarryIt · Live tracking</div>
      </header>
      <main className="max-w-2xl mx-auto px-4 pb-10 space-y-4">
        <div className="rounded-3xl bg-white border p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Parcel for you</div>
              <h1 className="font-display text-2xl font-extrabold">{t.pickup_location} → {t.drop_location}</h1>
              <div className="text-sm text-slate-500 mt-1 truncate max-w-xs">{t.description}</div>
            </div>
            <Badge className={STATUS_STYLE[t.status] || "bg-slate-100"}>{t.status.replace("_"," ").toUpperCase()}</Badge>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm flex-wrap">
            <PackageCheck className="w-4 h-4 text-slate-500"/>
            <span>Carrier: <span className="font-semibold">{t.carrier_name}</span></span>
            {t.carrier_rating > 0 && <span className="text-slate-500 inline-flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-400 stroke-amber-500"/> {t.carrier_rating}</span>}
            {t.eta?.available && !t.delivered && (
              <>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 text-xs">
                  {t.status === "in_transit"
                    ? `Arrival by ${new Date(t.eta.arrival_by).toLocaleString("en-IN", {month:"short", day:"numeric", hour:"numeric", minute:"2-digit"})}`
                    : `Estimated · ${t.eta.display}`}
                </span>
                {t.eta.same_day && <Badge className="bg-emerald-100 text-emerald-700">Same-day</Badge>}
              </>
            )}
          </div>
        </div>

        <TrackingMap pickup={t.pickup_location} drop={t.drop_location} progressPercent={t.progress_percent || 0} lastLocation={t.last_location}/>

        {t.drop_pin && (
          <div className="rounded-3xl bg-emerald-50 border border-emerald-200 p-5">
            <div className="text-xs uppercase text-emerald-800 tracking-wider flex items-center gap-1"><KeyRound className="w-3 h-3"/> Your drop PIN</div>
            <div className="font-mono text-4xl font-extrabold mt-1 tracking-widest" data-testid="public-drop-pin">{t.drop_pin}</div>
            <div className="text-xs text-emerald-800/80 mt-2">Share this PIN with the carrier only when they hand you the parcel.</div>
          </div>
        )}

        {t.delivered && (
          <div className="rounded-3xl bg-emerald-100 border border-emerald-300 p-5 text-emerald-900 text-center">
            <div className="font-display font-extrabold text-lg">Delivered ✓</div>
            <div className="text-sm">Thank you for using CarryIt.</div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicTrack;
